import { computeAllTemplateValues } from '../helpers/utils'

import type { Stripe } from 'stripe'
import type { Context, Flow, FlowMetadata, StripeCheckout2, UserData } from '../models/types'

export type StripeErrorLevel = 'warning' | 'error'
export type ErrorCategory = 'payment_issue' | 'config_issue' | 'transient' | 'system_error'

export interface ClassifiedStripeError {
  level: StripeErrorLevel
  isPaymentIssue: boolean
  category: ErrorCategory
  errorType: string
  message: string
  code?: string
}

type ReuseExistingCustomerResult = {
  customerId: string | null
  emailSource: 'checkout_session_customer_email' | 'confirm_checkout_options_email' | null
}

export type CreateNewCheckoutSessionResult = {
  session: Stripe.Checkout.Session
  omitConfirmCheckoutEmail: boolean
}

export function classifyStripeError(error: unknown): ClassifiedStripeError {
  //* Check if it's a Stripe error (has 'type' property from Stripe SDK)
  if (error && typeof error === 'object' && 'type' in error) {
    const stripeError = error as {
      type: string
      message: string
      code?: string
    }

    switch (stripeError.type) {
      case 'StripeCardError':
        //* Expected payment failures - end user's card issue
        return {
          level: 'warning',
          isPaymentIssue: true,
          category: 'payment_issue',
          errorType: stripeError.type,
          message: stripeError.message,
          code: stripeError.code,
        }

      case 'StripeInvalidRequestError':
        // Configuration issues by embeddable creator (e.g., "No such price")
        //* These are user config errors, not system bugs
        return {
          level: 'warning',
          isPaymentIssue: false,
          category: 'config_issue',
          errorType: stripeError.type,
          message: stripeError.message,
          code: stripeError.code,
        }

      case 'StripeConnectionError':
      case 'StripeRateLimitError':
        //* Transient issues - retry may succeed
        return {
          level: 'warning',
          isPaymentIssue: false,
          category: 'transient',
          errorType: stripeError.type,
          message: stripeError.message,
          code: stripeError.code,
        }

      case 'StripeAPIError':
      case 'StripeAuthenticationError':
      case 'StripePermissionError':
      default:
        //* System errors - needs engineering attention
        return {
          level: 'error',
          isPaymentIssue: false,
          category: 'system_error',
          errorType: stripeError.type,
          message: stripeError.message,
          code: stripeError.code,
        }
    }
  }

  // Non-Stripe errors are treated as system errors
  return {
    level: 'error',
    isPaymentIssue: false,
    category: 'system_error',
    errorType: 'ApplicationError',
    message: error instanceof Error ? error.message : String(error),
  }
}

// ----------------------

export async function getFlowJson(
  context: Context,
  flowId: string,
  version: string
): Promise<Flow> {
  const path = `${flowId}@${version}`

  const flowJsonString =
    (await context.flowsBucket.get(path)) ||
    (await fetch(`https://flow-storage.heysavvy.workers.dev/flows/${path}`).then((res) =>
      res.text()
    ))

  if (!flowJsonString) throw new Error(`Flow JSON not found for flow ${flowId}`)

  try {
    const flowJson = JSON.parse(flowJsonString) as Flow

    if (!flowJson || Object.keys(flowJson).length === 0) {
      throw new Error(`Flow JSON is empty for flow ${flowId}`)
    }

    return flowJson
  } catch (error) {
    console.error('error', error)
    throw new Error(`Flow JSON is not valid JSON for flow ${flowId}: ${error}`)
  }
}

export async function getFlowMetadata(context: Context, flowId: string): Promise<FlowMetadata> {
  const flowMetadata =
    (await context.flowsMetadataBucket.get(flowId)) ||
    (await fetch(`https://flow-metadata-storage.heysavvy.workers.dev/flows/${flowId}`).then((res) =>
      res.text()
    ))

  if (!flowMetadata) throw new Error(`Flow metadata not found for flow ${flowId}`)

  try {
    const flowMetadataJson = JSON.parse(flowMetadata) as FlowMetadata

    if (!flowMetadataJson || Object.keys(flowMetadataJson).length === 0) {
      throw new Error(`Flow metadata is empty for flow ${flowId}`)
    }

    return flowMetadataJson
  } catch (error) {
    throw new Error(`Flow metadata is not valid JSON for flow ${flowId}: ${error}`)
  }
}

export async function getProjectId(context: Context, flowId: string): Promise<string> {
  const flowMetadata = await getFlowMetadata(context, flowId)
  return flowMetadata.project_id
}

export async function getStripeComponent(
  context: Context,
  flowId: string,
  flowVersion: string,
  componentId: string
): Promise<StripeCheckout2> {
  const flowJson = await getFlowJson(context, flowId, flowVersion)

  const allComponents = [
    ...(flowJson.components || []),
    ...(flowJson.pages || []).flatMap((page) => page.components || []),
  ]
  const component = allComponents.find((component) => component.id === componentId)

  if (!component) throw new Error(`Stripe component not found for component ${componentId}`)

  return component as StripeCheckout2
}

export async function getStripeKey(
  context: Context,
  projectId: string,
  env: 'dev' | 'prod',
  keyType: 'restricted_key' | 'public_key'
): Promise<string | undefined> {
  if (!context.environment) {
    throw new Error('environment is not available')
  }

  const CREDS_WORKER = context.environment.CREDS_WORKER

  try {
    const { value } = await CREDS_WORKER.getSecretKey(projectId, keyType, env)

    return value
  } catch (error) {
    if (env === 'dev') return getTestStripeKey(context, keyType)

    throw new Error(`Stripe ${keyType} key is not available for project ${projectId}: ${error}`)
  }
}

function getTestStripeKey(context: Context, keyType: 'restricted_key' | 'public_key'): string {
  if (!context.environment) {
    throw new Error('environment is not available')
  }

  switch (keyType) {
    case 'restricted_key': {
      return context.environment.STRIPE_TEST_RESTRICTED_KEY
    }
    case 'public_key': {
      return context.environment.STRIPE_TEST_PUBLIC_KEY
    }
    default:
      throw new Error(`Invalid key type: ${keyType}`)
  }
}

export async function createNewCheckoutSession(
  context: Context,
  stripe: Stripe,
  checkoutSessionFromComponent: Stripe.Checkout.SessionCreateParams,
  userData: UserData,
  confirmCheckoutOptionsFromComponent?: StripeCheckout2['confirm_checkout_options'],
  originUrl?: string,
  disableReuseExistingStripeCustomerByEmail = false
): Promise<CreateNewCheckoutSessionResult> {
  const checkoutSessionCreateParams = computeAllTemplateValues(
    checkoutSessionFromComponent,
    userData
  ) as Stripe.Checkout.SessionCreateParams
  const confirmCheckoutOptions = computeAllTemplateValues(
    confirmCheckoutOptionsFromComponent || {},
    userData
  ) as StripeCheckout2['confirm_checkout_options']

  context.logger?.info({
    scope: 'Payments v2',
    functionName: 'createNewCheckoutSession',
    message: 'Creating checkout session with create params',
    props: {
      checkoutSessionFromComponent,
      userData,
      checkoutSessionCreateParams,
    },
  })

  const isKlarna = checkoutSessionCreateParams.payment_method_types?.includes('klarna')
  const overriddenReturnUrl = checkoutSessionCreateParams.return_url

  const reuseExistingCustomerResult = await reuseExistingCustomerByEmail({
    stripe,
    checkoutSessionCreateParams,
    confirmCheckoutOptions,
    disableReuseExistingStripeCustomerByEmail,
  })

  //* If the payment method includes Klarna
  //TODO: remove the !overriddenReturnUrl check once the team migrated the flow with workaround.
  if (isKlarna && !overriddenReturnUrl) {
    let url: URL | undefined
    //* If the return url is set, we need to use it
    if (overriddenReturnUrl) url = new URL(overriddenReturnUrl)
    //* If the return url is not set, we need to set it to the origin url
    if (!overriddenReturnUrl && originUrl) url = new URL(originUrl)
    //* If the url is set, we need to set the return url to the url with the session_id parameter
    if (url) {
      //* Delete the session_id parameter if it exists
      url.searchParams.delete('session_id')
      //* Set the session_id parameter to the checkout session id
      url.searchParams.set('session_id', '{CHECKOUT_SESSION_ID}')
      //* Return the new url
      checkoutSessionCreateParams.return_url = url
        .toString()
        .replace('%7BCHECKOUT_SESSION_ID%7D', '{CHECKOUT_SESSION_ID}')
    }
  }
  //* If the return url is set but Klarna is not the payment method, we need to use it
  else if (overriddenReturnUrl) checkoutSessionCreateParams.return_url = overriddenReturnUrl

  const finalCheckoutSessionCreateParams: Stripe.Checkout.SessionCreateParams = {
    ui_mode: 'custom',
    ...checkoutSessionCreateParams,
  } as any

  // If 'discounts' is present, or if the mode is 'setup', Stripe doesn't allow 'allow_promotion_codes' to be true
  if (!checkoutSessionCreateParams.discounts && checkoutSessionCreateParams.mode !== 'setup') {
    finalCheckoutSessionCreateParams.allow_promotion_codes = true
  }

  context.logger?.info({
    scope: 'Payments v2',
    functionName: 'createNewCheckoutSession',
    message: 'Creating checkout session with final create params',
    props: {
      finalCheckoutSessionCreateParams,
    },
  })

  const session = await stripe.checkout.sessions.create(finalCheckoutSessionCreateParams)

  return {
    session,
    omitConfirmCheckoutEmail:
      reuseExistingCustomerResult.customerId !== null &&
      reuseExistingCustomerResult.emailSource === 'confirm_checkout_options_email',
  }
}

export async function findExistingCustomer(
  stripe: Stripe,
  customerCreateParams: Stripe.CustomerCreateParams
): Promise<Stripe.Customer | null> {
  try {
    if (!customerCreateParams.email) return null

    // Stripe can return multiple customers for the same email, so pick the newest
    // customer explicitly instead of depending on the API's current ordering.
    // Reuse checks intentionally inspect only the first Stripe page to keep lookup cost bounded.
    const customers = await stripe.customers.list({ email: customerCreateParams.email, limit: 100 })
    const sortedCustomers = [...(customers.data || [])].sort((firstCustomer, secondCustomer) => {
      return secondCustomer.created - firstCustomer.created
    })

    return sortedCustomers[0] || null
  } catch (error) {
    console.error(error)
    // Customer lookup failures should only skip reuse; Checkout can still proceed unattached.
    return null
  }
}

async function reuseExistingCustomerByEmail({
  stripe,
  checkoutSessionCreateParams,
  confirmCheckoutOptions,
  disableReuseExistingStripeCustomerByEmail,
}: {
  stripe: Stripe
  checkoutSessionCreateParams: Stripe.Checkout.SessionCreateParams
  confirmCheckoutOptions?: StripeCheckout2['confirm_checkout_options']
  disableReuseExistingStripeCustomerByEmail: boolean
}): Promise<ReuseExistingCustomerResult> {
  const emptyResult: ReuseExistingCustomerResult = { customerId: null, emailSource: null }

  if (disableReuseExistingStripeCustomerByEmail) return emptyResult

  if (checkoutSessionCreateParams.customer) return emptyResult

  const { email, emailSource } = getCheckoutSessionCustomerEmail(
    checkoutSessionCreateParams,
    confirmCheckoutOptions
  )
  if (!email) return emptyResult

  const existingCustomer = await findExistingCustomer(stripe, { email })

  if (!existingCustomer) return emptyResult

  checkoutSessionCreateParams.customer = existingCustomer.id
  // Once an existing customer is attached, Stripe rejects params that are only
  // valid when Checkout is creating a customer from email.
  delete checkoutSessionCreateParams.customer_creation
  delete checkoutSessionCreateParams.customer_email
  return {
    customerId: existingCustomer.id,
    emailSource,
  }
}

function getCheckoutSessionCustomerEmail(
  checkoutSessionCreateParams: Stripe.Checkout.SessionCreateParams,
  confirmCheckoutOptions?: StripeCheckout2['confirm_checkout_options']
): {
  email: string | undefined
  emailSource: ReuseExistingCustomerResult['emailSource']
} {
  if (typeof checkoutSessionCreateParams.customer_email === 'string') {
    return {
      email: checkoutSessionCreateParams.customer_email,
      emailSource: 'checkout_session_customer_email',
    }
  }

  if (typeof confirmCheckoutOptions?.email === 'string') {
    return {
      email: confirmCheckoutOptions.email,
      emailSource: 'confirm_checkout_options_email',
    }
  }

  return { email: undefined, emailSource: null }
}

export async function createStripeCustomer(
  stripe: Stripe,
  customerFromComponent: Stripe.CustomerCreateParams,
  userData: UserData
): Promise<Stripe.Customer> {
  const customerCreateParams = computeAllTemplateValues(
    customerFromComponent,
    userData
  ) as Stripe.CustomerCreateParams

  const existingCustomer = await findExistingCustomer(stripe, customerCreateParams)

  if (existingCustomer) {
    return existingCustomer
  }

  //todo - find out what happens if an error is returned from stripe
  return stripe.customers.create(customerCreateParams)
}

export function createStripePaymentIntent(
  stripe: Stripe,
  paymentIntentFromComponent: Stripe.PaymentIntentCreateParams,
  userData: UserData
): Promise<Stripe.PaymentIntent> {
  const paymentIntentCreateParams = computeAllTemplateValues(
    paymentIntentFromComponent,
    userData
  ) as Stripe.PaymentIntentCreateParams

  //todo - find out what happens if an error is returned from stripe
  return stripe.paymentIntents.create(paymentIntentCreateParams)
}

export function createStripeSetupIntent(
  stripe: Stripe,
  setupIntentFromComponent: Stripe.SetupIntentCreateParams,
  userData: UserData
): Promise<Stripe.SetupIntent> {
  const setupIntentCreateParams = computeAllTemplateValues(
    setupIntentFromComponent,
    userData
  ) as Stripe.SetupIntentCreateParams

  //todo - find out what happens if an error is returned from stripe
  return stripe.setupIntents.create({
    ...setupIntentCreateParams,
    automatic_payment_methods: { enabled: true },
  })
}

export function createStripeSubscription(
  stripe: Stripe,
  subscriptionFromComponent: Stripe.SubscriptionCreateParams,
  userData: UserData
): Promise<Stripe.Subscription> {
  const subscriptionCreateParams = computeAllTemplateValues(
    subscriptionFromComponent,
    userData
  ) as Stripe.SubscriptionCreateParams

  //todo - find out what happens if an error is returned from stripe
  return stripe.subscriptions.create({
    ...subscriptionCreateParams,
    expand: ['latest_invoice.payment_intent', 'pending_setup_intent'],
  })
}

export async function retrieveCheckoutSession(
  stripe: Stripe,
  sessionId: string
): Promise<Stripe.Checkout.Session> {
  return stripe.checkout.sessions.retrieve(sessionId)
}

//
// ----------------------
//

// export function handleStripeError(
//   context: Context,
//   error: any,
//   flowId: string,
//   flowTitle: string
// ): ReturnVal {
//   const message = `${flowTitle} (${flowId}) encountered a Stripe Error: ${error.message}`

//   //   sendMessageSlack(process.env.IS_LOCALHOST ? 'C01527NKEF6' : 'C02QC92CGM8', message) //todo - replacing this with following lines. Not completelly sure it's correct.
//   const IS_LOCALHOST = false //@TODO - need to calculate this
//   notify(context, message, IS_LOCALHOST ? 'C01527NKEF6' : 'C02QC92CGM8')

//   switch (error.type) {
//     case 'StripePermissionError': {
//       return {
//         success: false,
//         errorMessage: 'This action is unauthorized - please see the site owner for details.',
//         statusCode: 401,
//       }
//     }

//     default: {
//       return { success: false, errorMessage: error.message, statusCode: 500 }
//     }
//   }
// }
